import { Location } from '@angular/common';
import { Injectable } from '@angular/core';

import { SetTab } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { UrlService } from '@frontend/vanilla/core';
import { find, isNaN, toNumber } from 'lodash-es';

import { EventMarketsTabBarItem } from '../../event-details-common/header/tab-group/event-header-tab-bar.models';
import { MarketUrlParam } from '../../navigation-core/url-helper.service';

@Injectable({ providedIn: 'root' })
export class EventDetailsMarketQueryService {
    private marketUrlToSetTab: StringDictionary<any> = {
        [MarketUrlParam.BetBuilder]: SetTab.BetBuilder,
        [MarketUrlParam.MyBets]: SetTab.MyBets,
        [MarketUrlParam.Sgp]: SetTab.Sgp,
    };

    constructor(
        private urlService: UrlService,
        private location: Location,
        private eventDetailConfig: EventDetailsConfig,
    ) {}

    getMarketSetFromQuery<T>(): T | undefined {
        const queryMarket = this.getQueryString();

        if (queryMarket) {
            const splitTags = this.getTagsFromQueryString();
            const lastTag = splitTags.pop();

            const parsed = lastTag ? this.marketUrlToSetTab[lastTag] || toNumber(lastTag) : undefined;

            if (isNaN(parsed) && this.eventDetailConfig.enableSitemapNavigation) {
                return lastTag as T;
            }

            return parsed;
        }

        return;
    }

    getQueryString(): string | undefined {
        const currentUrl = this.urlService.current();
        let market = currentUrl.search.get('market');
        if (market) {
            market = this.marketUrlToSetTab[market]?.toString() ?? market;
        }

        return market ?? undefined;
    }

    getTagsFromQueryString(queryString?: string): string[] {
        const splitTags = (queryString ? queryString.split(':') : this.getQueryString()?.split(':')) ?? [];
        const tags = [];

        for (const tag of splitTags) {
            if (tag === '') {
                break;
            }

            tags.push(tag);
        }

        return tags;
    }

    cleanMyBetsQueryString(setTabs: EventMarketsTabBarItem[]): void {
        const current = this.urlService.current();
        if (!setTabs.find((tab) => tab.id === SetTab.MyBets) && current.search.get('market') === MarketUrlParam.MyBets) {
            current.search.delete('market');
            this.location.replaceState(current.path(), current.search.toString());
        }
    }

    cleanQueryString(cleanLastTag = false): void {
        const current = this.urlService.current();
        const marketInUrl = current.search.get('market');

        if (marketInUrl) {
            const tags = marketInUrl.split(':');
            if (cleanLastTag && tags.length > 1) {
                tags.pop();
                current.search.set('market', tags.join(':'));
                this.location.replaceState(current.path(), current.search.toString());

                return;
            }
        }

        current.search.delete('market');
        this.location.replaceState(current.path(), current.search.toString());
    }

    setQueryString(setTabs: EventMarketsTabBarItem[], activeTab?: EventMarketsTabBarItem): void {
        const active = activeTab ?? find(setTabs, (tab) => !!tab.active);
        let replace = false;

        if (!active) {
            return;
        }

        const current = this.urlService.current();
        const market = current.search.get('market');
        const currentMarket = active.queryString ?? active.id.toString();

        if (active.id === SetTab.BetBuilder || active.id === SetTab.MyBets) {
            if (current.search.has('promotedTemplate')) {
                current.search.delete('promotedTemplate');
                replace = true;
            }

            if (active.id === SetTab.BetBuilder && market !== MarketUrlParam.BetBuilder) {
                current.search.set('market', MarketUrlParam.BetBuilder);
                replace = true;
            }

            if (active.id === SetTab.MyBets && market !== MarketUrlParam.MyBets) {
                current.search.set('market', MarketUrlParam.MyBets);
                replace = true;
            }
        } else if (active.isSgp && !this.eventDetailConfig.enableSitemapNavigation) {
            current.search.set('market', MarketUrlParam.Sgp);
            replace = true;
        } else if (market !== currentMarket) {
            current.search.set('market', currentMarket);
            replace = true;
        }

        if (replace) {
            this.location.replaceState(current.path(), current.search.toString());
        }
    }
}
